/**
 * PDF-Lib CDN Loader - Direct CDN Loading
 * =======================================
 * Loads pdf-lib directly from CDN with proper error handling
 */

// Type definitions for pdf-lib
interface PDFLib {
  PDFDocument: any;
  rgb: any;
  StandardFonts: any;
  [key: string]: any;
}

// Global variable to store pdf-lib instance
let pdfLibInstance: PDFLib | null = null;

const trace = false;

/**
 * Load CDN scripts and wait for them to be available
 */
async function loadCDNScripts(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Vérifier si déjà chargé
    if (window.PDFLib) {
      if (trace) {
        console.log("✅ PDFLib déjà disponible");
      }
      resolve();
      return;
    }

    if (trace) {
      console.log("🔄 Chargement des scripts CDN pdf-lib...");
    }

    const script1 = document.createElement("script");
    script1.src = "https://unpkg.com/pdf-lib@1.17.1/dist/pdf-lib.min.js";
    script1.onload = () => {
      if (trace) {
        console.log("✅ pdf-lib.min.js chargé");
      }

      const script2 = document.createElement("script");
      script2.src =
        "https://unpkg.com/@pdf-lib/standard-fonts@1.0.0/dist/standard-fonts.min.js";
      script2.onload = () => {
        if (trace) {
          console.log("✅ standard-fonts.min.js chargé");
          console.log("✅ PDFLib disponible:", typeof window.PDFLib);
        }
        resolve();
      };
      script2.onerror = () => {
        console.error("❌ Erreur chargement standard-fonts");
        reject(new Error("Failed to load standard-fonts"));
      };
      document.head.appendChild(script2);
    };
    script1.onerror = () => {
      console.error("❌ Erreur chargement pdf-lib");
      reject(new Error("Failed to load pdf-lib"));
    };
    document.head.appendChild(script1);
  });
}

/**
 * Load pdf-lib from CDN
 */
export async function loadPDFLib(): Promise<PDFLib> {
  if (pdfLibInstance) {
    return pdfLibInstance;
  }

  try {
    // Charger les scripts CDN
    await loadCDNScripts();

    // Attendre que PDFLib soit disponible
    let attempts = 0;
    const maxAttempts = 50;

    while (!window.PDFLib && attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;

      if (attempts % 10 === 0) {
        console.log(`⏳ Attente PDFLib... (${attempts}/${maxAttempts})`);
      }
    }

    if (!window.PDFLib) {
      throw new Error(`PDFLib not available after ${maxAttempts * 100}ms`);
    }

    // Vérifier les composants nécessaires
    if (!window.PDFLib.PDFDocument) {
      throw new Error("PDFDocument not available in PDFLib");
    }
    if (!window.PDFLib.StandardFonts) {
      throw new Error("StandardFonts not available in PDFLib");
    }

    pdfLibInstance = window.PDFLib;
    if (trace) {
      console.log("✅ Successfully loaded pdf-lib via CDN");
      console.log("📊 PDFDocument:", typeof window.PDFLib.PDFDocument);
      console.log("📊 StandardFonts:", typeof window.PDFLib.StandardFonts);
    }
    return pdfLibInstance;
  } catch (error) {
    console.error("❌ Failed to load pdf-lib via CDN:", error);
    throw new Error(
      `pdf-lib not available via CDN: ${(error as Error).message}`
    );
  }
}

/**
 * Get PDFDocument from loaded pdf-lib
 */
export async function getPDFDocument() {
  const pdfLib = await loadPDFLib();
  return pdfLib.PDFDocument;
}

/**
 * Get rgb function from loaded pdf-lib
 */
export async function getRGB() {
  const pdfLib = await loadPDFLib();
  return pdfLib.rgb;
}

/**
 * Get StandardFonts from loaded pdf-lib
 */
export async function getStandardFonts() {
  const pdfLib = await loadPDFLib();
  return pdfLib.StandardFonts;
}

// Note: PDFLib global declaration is handled elsewhere in the project
